import classNames from "classnames";
import { DateTime } from "luxon";
import React from "react";

import { LISTask } from "pwapi";

import { DateDiff } from "shared/utils/dates";

import { useAppSelector } from "app/hooks";
import { useSelectedLab } from "hooks/useSelectedLabStation";
import AccessionNumber from "ui/components/AccessionNumber";
import CasePriority from "ui/components/CasePriority";
import ClinicName from "ui/components/ClinicName";
import PatientFullName from "ui/components/PatientFullName";
import UserFullName from "ui/components/UserFullName";

const CaseListItem = ({
  id,
  label,
  stationType,
  timeInStation,
  children,
  onClick,
  className,
  warning,
}: {
  id: number;
  label?: string;
  stationType?: LISTask.type;
  timeInStation?: DateDiff | null;
  children?: React.ReactNode;
  onClick?: (ev: any) => void;
  className?: string;
  warning?: React.ReactNode;
}) => {
  const pathCase = useAppSelector((state) => state.casesWithWork[id]);
  const lab = useSelectedLab();
  const clinic = useAppSelector((state) =>
    pathCase?.patient?.clinic_id
      ? state.clinics[pathCase.patient.clinic_id]
      : undefined
  );
  const displayTimeInStation = lab?.settings?.display_time_in_station;
  const timeInStationStr =
    !!displayTimeInStation?.active && timeInStation?.timeStr;
  const timeInStationHours = timeInStation?.hours ?? 0;
  const warningThreshold = (
    displayTimeInStation?.details as {
      [key: string]: { warning: number; danger: number };
    } | null
  )?.[stationType ?? "default"] ?? { warning: 24, danger: 36 };

  const birthdayStr = pathCase?.patient.date_of_birth
    ? DateTime.fromFormat(
        pathCase.patient.date_of_birth,
        "yyyy-MM-dd"
      ).toLocaleString()
    : "";

  if (!pathCase) return null;

  return (
    <div className={`CaseListItem ${className || ""}`} onClick={onClick}>
      <div className="CaseListItem__row">
        <code className="CaseListItem__accession">
          <CasePriority priorityCase={pathCase} />
          <AccessionNumber accession={pathCase.accession} />
          {lab.settings?.new_york_lis_tag &&
            clinic?.location?.state === "NY" && (
              <div className="Tag Tag--purple">New York</div>
            )}
        </code>
        <strong>
          <PatientFullName patient={pathCase.patient} />
        </strong>
        <strong>
          <ClinicName id={pathCase.patient.clinic_id} />
        </strong>
      </div>
      <div className="CaseListItem__row">
        <span className="CaseListItem__label">
          <span
            className={classNames({
              "CaseListItem__label--warning":
                timeInStationHours >= warningThreshold.warning &&
                timeInStationHours < warningThreshold.danger,
              "CaseListItem__label--danger":
                timeInStationHours >= warningThreshold.danger,
            })}
          >
            {timeInStationStr}
          </span>
          {!!timeInStationStr && !!label && " -"}
          <em>{label}</em>
        </span>
        <div>{birthdayStr}</div>
        <div>
          <UserFullName
            id={pathCase.ordering_doctor_id}
            showCredentials={true}
          />
        </div>
      </div>
      {warning && <div className="WorkList__item__warning">{warning}</div>}
      {children && <div className="CaseListItem__children">{children}</div>}
    </div>
  );
};

export default CaseListItem;
